import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Oauth2Service } from '@bytel/checkout';

import { ENV_LIST, IInitialConfig } from '@interfaces/config.interface';

import { AsyncSubject, Observable, catchError, mergeMap, share } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { BasicObject } from '../models/services.interface';
import { BtlPrismeService } from '../services/btl-prisme.service';
import { CustomHttpParamEncoder } from './custom-http-param-encoder.class';

@Injectable({
    providedIn: 'root',
})
export class Oauth2ResourcesService {
    public static HTTP_UNAUTHORIZED: number = 401;

    public inService: boolean = false;
    public store;

    protected headers: BasicObject = {};
    protected localService: boolean = false;
    protected params: HttpParams;
    protected responseType: 'arraybuffer' | 'blob' | 'json' | 'text';
    protected resourceUrl: string = '';

    private _initialConfig: IInitialConfig;

    constructor(
        protected oauth2: Oauth2Service,
        protected http: HttpClient,
        protected prismeService: BtlPrismeService,
    ) {
        this.params = new HttpParams({ encoder: new CustomHttpParamEncoder() });
        this._initialConfig = window.ConfigInitial;
    }

    public addCache(): this {
        return this.addHeaders({ 'Cache-Control': 'cache-js' });
    }

    public addHeaders(headers: BasicObject): this {
        const resource: this = this.getInstance();
        resource.headers = Object.assign(resource.headers, headers);
        return resource;
    }

    public adresseLivraison(): this {
        return this.getResource('/adresses-livraison');
    }

    public clickAndCollect(): this {
        return this.getResource('/click-and-collect');
    }

    public pointRelais(): this {
        return this.getResource('/points-relais');
    }

    public adresseFacturation(): this {
        return this.getResource('/adresse-facturation');
    }

    public commande(id?: number | string): this {
        let resourceUrl = '/commande';
        if (id) {
            resourceUrl += '/' + id.toString();
        }
        return this.getResource(resourceUrl);
    }

    public controlePortabilite(): this {
        return this.getResource('/controle-portabilite');
    }

    public creneauxDisponibles(idPanier: string): this {
        return this.getResource(`/creneaux-disponibles?idPanier=${idPanier}`);
    }

    public creneauxLivraison(): this {
        return this.getResource('/creneaux-livraison');
    }

    public recherche(): this {
        return this.getResource('/recherche');
    }

    public delete<T = any>(): Observable<T> {
        return this.getRequest('DELETE');
    }

    public eligibilite(idPanier?: string): this {
        const resourceUrl = `/eligibilite${idPanier ? '/' + idPanier : ''}`;
        return this.getResource(resourceUrl);
    }

    public get<T = any>(): Observable<T> {
        return this.getRequest('GET');
    }

    public getClient(): HttpClient {
        return this.http;
    }

    public getUrl(): string {
        return this._getRequestUrl() + this.resourceUrl;
    }

    public iban(): this {
        return this.getResource('/iban');
    }

    public listeProduits(): this {
        return this.getResource('/liste-produits');
    }

    public modesLivraison(): this {
        return this.getResource('/modes-livraison');
    }

    public livraisonsDispo(): this {
        return this.getResource('/livraisons-disponibles');
    }

    public modesPaiement(): this {
        return this.getResource('/modes-paiement');
    }

    public modesFinancement(): this {
        return this.getResource('/modes-financement');
    }

    public courant(): this {
        return this.getResource('/courant');
    }

    public offres(id?: number | string): this {
        const resourceUrl = `/offres${id ? '/' + id.toString() : ''}`;
        return this.getResource(resourceUrl);
    }

    public panier(id?: number | string): this {
        const resourceUrl = `/paniers${id ? '/' + id.toString() : ''}`;
        return this.getResource(resourceUrl);
    }

    public parcours(id?: number | string): this {
        const resourceUrl = `/parcours${id ? '/' + id.toString() : ''}`;
        return this.getResource(resourceUrl);
    }

    public personnes(personnesId?: number | string): this {
        let resourceUrl = '/personnes';
        if (personnesId) {
            resourceUrl += '/' + personnesId;
        }
        return this.getResource(resourceUrl);
    }

    public post(body: {}): Observable<any> {
        return this.getRequest('POST', body);
    }

    public produits(id?: number | string): this {
        const resourceUrl = `/produits${id ? '/' + id : ''}`;
        return this.getResource(resourceUrl);
    }

    public promotions(id?: number): this {
        const resourceUrl = `/promotions${id ? '/' + id : ''}`;
        return this.getResource(resourceUrl);
    }

    public simsContextualises(): this {
        return this.getResource(`/catalogue/sims-contextualises`);
    }

    public put<T = any>(body: { [index: string]: any }): Observable<T> {
        return this.getRequest('PUT', body);
    }

    public referentiel(): this {
        return this.getResource('/referentiel');
    }

    public ressources(): this {
        return this.getResource('/ressources');
    }

    public scoring(): this {
        return this.getResource('/scoring');
    }

    public scoringV3(): this {
        return this.getResource('/controles-risques-vente/autorisation-vente');
    }

    public session(): this {
        return this.getResource('/session');
    }

    public loginIframe(): Observable<boolean> {
        return this.oauth2.loginIframeOauth2();
    }

    public setLocalService(flag: boolean = true): this {
        const resource: this = this.getInstance();
        resource.localService = flag;
        return resource;
    }

    public setParams(params: { [index: string]: string | number }): this {
        const resource: this = this.getInstance();
        Object.keys(params).forEach((key: string) => {
            resource.params = resource.params.set(key, '' + params[key]);
        });
        return resource;
    }

    public setResponseType(value: 'arraybuffer' | 'blob' | 'json' | 'text'): this {
        const resource: this = this.getInstance();
        resource.responseType = value;
        return resource;
    }

    public setUrl(resourceUrl: string): this {
        const resource: this = this.getInstance();
        resource.resourceUrl += resourceUrl;
        return resource;
    }

    public techno(techno: string): this {
        return this.getResource('/' + techno);
    }

    public useSalesApi(): this {
        const resource: this = this.getInstance();
        resource.headers['x-version'] = this._initialConfig.oauth.salesApiVersion || '4';
        return resource;
    }

    public useCorbisApi(): this {
        const resource: this = this.getInstance();
        resource.headers['x-version'] = '1';
        return resource;
    }

    public ventes(): this {
        this.setLocalService();
        return this.getResource('/ventes');
    }

    public medocDocumentLinks(path: string): this {
        return this.getResource(path);
    }
    public medocDocumentPrecontractuels(cartId: number): this {
        return this.getResource(`/documents/documents-precontractuels?idPanier=${cartId}`);
    }

    public openBankingVerification(): this {
        return this.getResource('/verifications-bancaires/demande');
    }

    public openBankingCheckVerification(idPu: string): this {
        return this.getResource(`/verifications-bancaires?noPersonne=${idPu}`);
    }

    public createCorbisCart(): this {
        return this.getResource('/paniers-commande-fixe');
    }

    protected _getRequestOptions(): {} {
        this.setTrackerId();

        if (
            this._initialConfig.environment !== ENV_LIST.PRODUCTION &&
            this._initialConfig.environment !== ENV_LIST.LOCAL
        ) {
            this.headers['X-banc'] = this._initialConfig.oauth.xBanc;
        }
        if (this.oauth2.access_token) {
            this.headers.Authorization = `Bearer ${this.oauth2.access_token}`;
        }
        return {
            headers: new HttpHeaders(this.headers),
            params: this.params,
            responseType: this.responseType,
        };
    }

    protected _getRequestUrl(): string {
        return this._initialConfig.oauth.resourceUrl;
    }

    protected getInstance(): this {
        if (this.inService) {
            return this;
        } else {
            const instance: this = new (this.constructor as any)(
                this.oauth2,
                this.getClient(),
                this.prismeService,
            ) as this;
            instance.inService = true;
            return instance;
        }
    }

    protected getRequest(httpMethod: string, body?: BasicObject): Observable<any> {
        const http: HttpClient = this.http;
        let obs: Observable<any>;
        switch (httpMethod) {
            case 'POST':
                obs = http.post(this.getUrl(), body, this._getRequestOptions());
                break;

            case 'PUT':
                obs = http.put(this.getUrl(), body, this._getRequestOptions());
                break;

            case 'GET':
                obs = http.get(this.getUrl(), this._getRequestOptions());
                break;

            case 'DELETE':
                obs = http.delete(this.getUrl(), this._getRequestOptions());
                break;

            default:
                throw Error('Unknow HTTP method');
        }

        return obs.pipe(
            catchError((res: any) => {
                if (res.status === Oauth2ResourcesService.HTTP_UNAUTHORIZED) {
                    return this.oauth2.login().pipe(mergeMap(() => obs));
                }

                throw res;
            }),
            share({
                connector: () => new AsyncSubject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false,
            }),
        );
    }

    protected getResource(resourceUrl: string): this {
        const resource: this = this.getInstance();
        resource.resourceUrl += resourceUrl;
        resource._addSpecialHeader();

        return resource;
    }

    protected setTrackerId(): this {
        this.headers.TrackerId = this.prismeService.getSession();
        return this;
    }

    private _addSpecialHeader(): this {
        const resource: this = this.getInstance();

        const xSource = this._initialConfig.oauth.xSource || 'MAGENTO';
        const headers: BasicObject = {
            'X-source': xSource,
            Source: xSource,
            'X-Message-Id': uuidv4(),
        };

        resource.addHeaders(headers);

        return resource;
    }
}
